/**
 * Make & Model Management Page
 * Multi-country vehicle data management with enable/disable per country
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import {
  ArrowLeft, Car, Save, Plus, Trash2, Edit,
  Search, Upload, Download, RefreshCw, Globe,
  Check, X, ChevronDown, Database, Star, Eye, EyeOff
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'SL', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'EU', name: 'European Union', flag: '🇪🇺' }
];

const VEHICLE_TYPES = [
  { slug: 'cars', name: 'Cars', icon: '🚗' },
  { slug: 'motorcycles-scooters', name: 'Motorcycles', icon: '🏍️' },
  { slug: 'three-wheelers', name: 'Three-Wheelers', icon: '🛺' },
  { slug: 'vans', name: 'Vans', icon: '🚐' },
  { slug: 'trucks-lorries', name: 'Trucks', icon: '🚚' },
  { slug: 'boats-watercraft', name: 'Boats', icon: '🚤' }
];

const MakeModelManagementPage = () => {
  const { toast } = useToast();
  const [selectedCountry, setSelectedCountry] = useState('SL');
  const [selectedVehicleType, setSelectedVehicleType] = useState('cars');
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMake, setExpandedMake] = useState(null);
  
  // Dialogs
  const [editMakeDialog, setEditMakeDialog] = useState({ open: false, make: null });
  const [editModelDialog, setEditModelDialog] = useState({ open: false, make: null, model: null });
  const [addMakeDialog, setAddMakeDialog] = useState(false);
  const [addModelDialog, setAddModelDialog] = useState({ open: false, makeId: null });
  const [seedDialog, setSeedDialog] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  
  // Form states
  const [newMake, setNewMake] = useState({ name: '', country_of_origin: '', vehicle_types: ['cars'] });
  const [newModel, setNewModel] = useState({ name: '', body_types: [], engine_types: [] });
  const [editCountryConfig, setEditCountryConfig] = useState({ enabled: true, popularity: 50, local_name: '' });

  // Fetch makes from API
  const fetchMakes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vehicles/admin/makes`, {
        params: { country: selectedCountry, include_disabled: true }
      });
      setMakes(response.data.makes || []);
    } catch (error) {
      console.error('Failed to fetch makes:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch vehicle makes',
        variant: 'destructive'
      });
      setMakes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, toast]);

  useEffect(() => {
    fetchMakes();
  }, [fetchMakes]);

  // Filter makes by search and vehicle type
  const filteredMakes = makes.filter(make => {
    const matchesSearch = make.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = make.vehicle_types?.includes(selectedVehicleType) || selectedVehicleType === 'all';
    return matchesSearch && matchesType;
  });

  // Check if make/model is enabled for current country
  const isEnabledForCountry = (item) => {
    return item.countries?.[selectedCountry]?.enabled !== false;
  };

  // Get country-specific config
  const getCountryConfig = (item) => {
    return item.countries?.[selectedCountry] || { enabled: true, popularity: 99 };
  };

  // Toggle make enabled status for country
  const toggleMakeEnabled = async (makeId, currentEnabled) => {
    try {
      await axios.patch(
        `${API_URL}/api/vehicles/admin/makes/${makeId}/country/${selectedCountry}?admin_id=admin`,
        { enabled: !currentEnabled }
      );
      toast({
        title: 'Success',
        description: `Make ${!currentEnabled ? 'enabled' : 'disabled'} for ${selectedCountry}`
      });
      fetchMakes();
    } catch (error) {
      console.error('Failed to toggle make:', error);
      // Optimistic update for demo
      setMakes(prev => prev.map(m => 
        m.id === makeId 
          ? { ...m, countries: { ...m.countries, [selectedCountry]: { ...m.countries?.[selectedCountry], enabled: !currentEnabled } } }
          : m
      ));
    }
  };

  // Update country config (popularity, local_name)
  const updateCountryConfig = async (makeId, config) => {
    try {
      await axios.patch(
        `${API_URL}/api/vehicles/admin/makes/${makeId}/country/${selectedCountry}?admin_id=admin`,
        config
      );
      toast({
        title: 'Success',
        description: 'Country configuration updated'
      });
      setEditMakeDialog({ open: false, make: null });
      fetchMakes();
    } catch (error) {
      console.error('Failed to update config:', error);
      toast({
        title: 'Error',
        description: 'Failed to update configuration',
        variant: 'destructive'
      });
    }
  };

  // Add new make
  const handleAddMake = async () => {
    if (!newMake.name) return;
    
    try {
      const makeData = {
        name: newMake.name,
        country_of_origin: newMake.country_of_origin,
        vehicle_types: newMake.vehicle_types,
        countries: {
          [selectedCountry]: { enabled: true, popularity: 50 }
        },
        models: []
      };
      
      await axios.post(`${API_URL}/api/vehicles/admin/makes?admin_id=admin`, makeData);
      toast({
        title: 'Success',
        description: `${newMake.name} added successfully`
      });
      setAddMakeDialog(false);
      setNewMake({ name: '', country_of_origin: '', vehicle_types: ['cars'] });
      fetchMakes();
    } catch (error) {
      console.error('Failed to add make:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add make',
        variant: 'destructive'
      });
    }
  };

  // Add model to make
  const handleAddModel = async () => {
    if (!newModel.name || !addModelDialog.makeId) return;
    
    try {
      const modelData = {
        name: newModel.name,
        body_types: newModel.body_types,
        engine_types: newModel.engine_types,
        countries: {
          [selectedCountry]: { enabled: true }
        }
      };
      
      await axios.post(
        `${API_URL}/api/vehicles/admin/makes/${addModelDialog.makeId}/models?admin_id=admin`,
        modelData
      );
      toast({
        title: 'Success',
        description: `${newModel.name} added successfully`
      });
      setAddModelDialog({ open: false, makeId: null });
      setNewModel({ name: '', body_types: [], engine_types: [] });
      fetchMakes();
    } catch (error) {
      console.error('Failed to add model:', error);
      toast({
        title: 'Error',
        description: 'Failed to add model',
        variant: 'destructive'
      });
    }
  };

  // Seed data
  const handleSeedData = async (country) => {
    setSeedLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/vehicles/admin/seed?country=${country}&admin_id=admin`
      );
      toast({
        title: 'Success',
        description: `Seeded ${response.data.inserted} new makes, updated ${response.data.updated}`
      });
      setSeedDialog(false);
      fetchMakes();
    } catch (error) {
      console.error('Failed to seed data:', error);
      toast({
        title: 'Error',
        description: 'Failed to seed data',
        variant: 'destructive'
      });
    } finally {
      setSeedLoading(false);
    }
  };

  // Get model count for country
  const getModelCount = (make) => {
    return make.models?.filter(m => m.countries?.[selectedCountry]?.enabled !== false).length || 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles/data">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Database className="h-6 w-6 text-blue-600" />
              Make & Model Management
            </h1>
            <p className="text-gray-500">Multi-country vehicle data with enable/disable per country</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSeedDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Seed Data
          </Button>
          <Button onClick={() => setAddMakeDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Make
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Country Selector */}
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-500" />
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.flag} {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Vehicle Type Selector */}
            <div className="flex items-center gap-2">
              <Car className="h-4 w-4 text-gray-500" />
              <Select value={selectedVehicleType} onValueChange={setSelectedVehicleType}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vehicle Types</SelectItem>
                  {VEHICLE_TYPES.map(vt => (
                    <SelectItem key={vt.slug} value={vt.slug}>
                      {vt.icon} {vt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search makes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Button variant="outline" onClick={fetchMakes}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">{makes.length}</div>
            <p className="text-sm text-gray-500">Total Makes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {makes.filter(m => isEnabledForCountry(m)).length}
            </div>
            <p className="text-sm text-gray-500">Enabled for {selectedCountry}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-purple-600">
              {makes.reduce((sum, m) => sum + (m.models?.length || 0), 0)}
            </div>
            <p className="text-sm text-gray-500">Total Models</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">
              {makes.reduce((sum, m) => sum + getModelCount(m), 0)}
            </div>
            <p className="text-sm text-gray-500">Models Enabled ({selectedCountry})</p>
          </CardContent>
        </Card>
      </div>

      {/* Makes List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="h-5 w-5" />
            Vehicle Makes for {COUNTRIES.find(c => c.code === selectedCountry)?.flag} {selectedCountry}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filteredMakes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Car className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No makes found. Try seeding data or add a new make.</p>
            </div>
          ) : (
            <Accordion type="single" collapsible value={expandedMake} onValueChange={setExpandedMake}>
              {filteredMakes.map((make) => {
                const config = getCountryConfig(make);
                const isEnabled = isEnabledForCountry(make);
                
                return (
                  <AccordionItem key={make.id} value={make.id} className="border rounded-lg mb-2 px-4">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          {/* Enable/Disable Toggle */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => toggleMakeEnabled(make.id, isEnabled)}
                            />
                          </div>
                          
                          <span className={`font-semibold text-lg ${!isEnabled ? 'text-gray-400' : ''}`}>
                            {config.local_name || make.name}
                            {config.local_name && config.local_name !== make.name && (
                              <span className="text-sm text-gray-400 ml-2">({make.name})</span>
                            )}
                          </span>
                          
                          <Badge variant={isEnabled ? 'default' : 'secondary'}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                          
                          <Badge variant="outline">
                            {getModelCount(make)} models
                          </Badge>
                          
                          {config.popularity <= 10 && (
                            <Badge className="bg-yellow-100 text-yellow-700">
                              <Star className="h-3 w-3 mr-1" />
                              Popular
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditCountryConfig(config);
                              setEditMakeDialog({ open: true, make });
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </AccordionTrigger>
                    
                    <AccordionContent>
                      <div className="pb-4 space-y-4">
                        {/* Make Info */}
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 border-b pb-4">
                          <span>Origin: {make.country_of_origin || 'N/A'}</span>
                          <span>Types: {make.vehicle_types?.join(', ') || 'N/A'}</span>
                          <span>Popularity: #{config.popularity || 99}</span>
                        </div>
                        
                        {/* Models */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium">Models</h4>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAddModelDialog({ open: true, makeId: make.id })}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Model
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {make.models?.map((model) => {
                              const modelConfig = model.countries?.[selectedCountry] || {};
                              const modelEnabled = modelConfig.enabled !== false;
                              
                              return (
                                <div
                                  key={model.id}
                                  className={`flex items-center justify-between p-2 border rounded ${
                                    modelEnabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    {modelEnabled ? (
                                      <Eye className="h-3 w-3 text-green-500" />
                                    ) : (
                                      <EyeOff className="h-3 w-3 text-gray-400" />
                                    )}
                                    <span className="text-sm">
                                      {modelConfig.local_name || model.name}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                            
                            {(!make.models || make.models.length === 0) && (
                              <p className="text-sm text-gray-400 col-span-full">No models defined</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Edit Make Country Config Dialog */}
      <Dialog open={editMakeDialog.open} onOpenChange={(open) => setEditMakeDialog({ open, make: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editMakeDialog.make?.name} for {selectedCountry}</DialogTitle>
            <DialogDescription>
              Configure country-specific settings for this make
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Local Name (optional)</Label>
              <Input
                placeholder={editMakeDialog.make?.name}
                value={editCountryConfig.local_name || ''}
                onChange={(e) => setEditCountryConfig(p => ({ ...p, local_name: e.target.value }))}
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                e.g., &quot;Vitz&quot; becomes &quot;Yaris&quot; in UK
              </p>
            </div>
            
            <div>
              <Label>Popularity Rank (1 = most popular)</Label>
              <Input
                type="number"
                min="1"
                max="999"
                value={editCountryConfig.popularity || 50}
                onChange={(e) => setEditCountryConfig(p => ({ ...p, popularity: parseInt(e.target.value) || 50 }))}
                className="mt-2"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Enabled for {selectedCountry}</Label>
              <Switch
                checked={editCountryConfig.enabled !== false}
                onCheckedChange={(checked) => setEditCountryConfig(p => ({ ...p, enabled: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMakeDialog({ open: false, make: null })}>
              Cancel
            </Button>
            <Button onClick={() => updateCountryConfig(editMakeDialog.make?.id, editCountryConfig)}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Make Dialog */}
      <Dialog open={addMakeDialog} onOpenChange={setAddMakeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Vehicle Make</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Make Name *</Label>
              <Input
                placeholder="e.g., Toyota"
                value={newMake.name}
                onChange={(e) => setNewMake(p => ({ ...p, name: e.target.value }))}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>Country of Origin</Label>
              <Input
                placeholder="e.g., Japan"
                value={newMake.country_of_origin}
                onChange={(e) => setNewMake(p => ({ ...p, country_of_origin: e.target.value }))}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>Vehicle Types</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {VEHICLE_TYPES.map(vt => (
                  <Badge
                    key={vt.slug}
                    variant={newMake.vehicle_types.includes(vt.slug) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setNewMake(p => ({
                        ...p,
                        vehicle_types: p.vehicle_types.includes(vt.slug)
                          ? p.vehicle_types.filter(t => t !== vt.slug)
                          : [...p.vehicle_types, vt.slug]
                      }));
                    }}
                  >
                    {vt.icon} {vt.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMakeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddMake} disabled={!newMake.name}>
              <Plus className="h-4 w-4 mr-2" />
              Add Make
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Model Dialog */}
      <Dialog open={addModelDialog.open} onOpenChange={(open) => setAddModelDialog({ open, makeId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Model</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Model Name *</Label>
              <Input
                placeholder="e.g., Corolla"
                value={newModel.name}
                onChange={(e) => setNewModel(p => ({ ...p, name: e.target.value }))}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>Body Types</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Sedan', 'SUV', 'Hatchback', 'Wagon', 'Coupe', 'Pickup', 'Van'].map(type => (
                  <Badge
                    key={type}
                    variant={newModel.body_types.includes(type.toLowerCase()) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setNewModel(p => ({
                        ...p,
                        body_types: p.body_types.includes(type.toLowerCase())
                          ? p.body_types.filter(t => t !== type.toLowerCase())
                          : [...p.body_types, type.toLowerCase()]
                      }));
                    }}
                  >
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Engine Types</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Petrol', 'Diesel', 'Hybrid', 'Electric'].map(type => (
                  <Badge
                    key={type}
                    variant={newModel.engine_types.includes(type.toLowerCase()) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      setNewModel(p => ({
                        ...p,
                        engine_types: p.engine_types.includes(type.toLowerCase())
                          ? p.engine_types.filter(t => t !== type.toLowerCase())
                          : [...p.engine_types, type.toLowerCase()]
                      }));
                    }}
                  >
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModelDialog({ open: false, makeId: null })}>
              Cancel
            </Button>
            <Button onClick={handleAddModel} disabled={!newModel.name}>
              <Plus className="h-4 w-4 mr-2" />
              Add Model
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Data Dialog */}
      <Dialog open={seedDialog} onOpenChange={setSeedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seed Vehicle Data</DialogTitle>
            <DialogDescription>
              Populate the database with predefined makes and models
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              This will add/update vehicle makes and models from the seed data.
              Existing data will be merged, not replaced.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {COUNTRIES.map(c => (
                <Button
                  key={c.code}
                  variant="outline"
                  className="h-20 flex-col gap-2"
                  onClick={() => handleSeedData(c.code)}
                  disabled={seedLoading}
                >
                  <span className="text-2xl">{c.flag}</span>
                  <span>Seed {c.code}</span>
                </Button>
              ))}
            </div>
            <Button
              className="w-full"
              onClick={() => handleSeedData('ALL')}
              disabled={seedLoading}
            >
              {seedLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Globe className="h-4 w-4 mr-2" />
              )}
              Seed All Countries
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MakeModelManagementPage;
